import { SITEMAP_GETTER_API_KEY } from './env/server';
import config from './config';

const metadata = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      home: {
        /**
         * The page title used in the <title> tag, this provides SEO keywords for a given page
         * and is used in google analytics data provided through the GTMService.
         */
        title: undefined as unknown as string, // should be overridden by site
        /**
         * This is used as a 'description' in a meta tag in the head and is also used in google analytics
         * data provided through the GTMService. The description is not placed within the UI of the page but
         * is instead used to allow crawlers to identify the topics of the page.
         */
        description: undefined as unknown as string, // should be overridden by site
        /**
         * The URL of the site favicon.
         */
        faviconURL: undefined as unknown as string, // should be overridden by site

        /**
         * Settings for search engine crawlers.
         *
         * @see https://developers.google.com/search/docs/crawling-indexing/robots/intro
         * @see https://nextjs.org/docs/app/api-reference/functions/generate-metadata#robots
         */
        robots: {
          /**
           * If this page should be indexed by search engines.
           */
          index: true
        }
      },

      search: {
        /**
         * The page title used in the <title> tag, this provides SEO keywords for a given page
         * and is used in google analytics data provided through the GTMService.
         *
         * @todo -- Added this based on current TEVA's production search page, but this should be
         * replaced by a more specific one in each site in the future.
         *
         * **IMPORTANT** -- Since this is a default placeholder, use as a template string to replace
         * `{term}` and `{site}` with the search term and site name respectively.
         *
         * To properly use it, combine with `replaceStringVariables` from {@link string-utils}.
         */
        title: 'Search for {term} | {site} ®',
        /**
         * This is used as a 'description' in a meta tag in the head and is also used in google analytics
         * data provided through the GTMService. The description is not placed within the UI of the page but
         * is instead used to allow crawlers to identify the topics of the page.
         *
         * @todo -- Right now we don't have anything set in TEVA or UGG production site, and HOKA adds a custom
         * generic description. I'm opting to leave a generic one for now, but this should be
         * replaced by a more specific one in each site in the future.
         *
         * **IMPORTANT** -- Since this is a default placeholder, use as a template string to replace
         * `{term}` and `{site}` with the search term and site name respectively.
         *
         * To properly use it, combine with `replaceStringVariables` from {@link string-utils}.
         */
        description: 'See what we found for {term} | {site} ®',
        /**
         * Settings for search engine crawlers.
         *
         * @see https://developers.google.com/search/docs/crawling-indexing/robots/intro
         * @see https://nextjs.org/docs/app/api-reference/functions/generate-metadata#robots
         */
        robots: {
          /**
           * If this page should be indexed by search engines.
           */
          index: true
        }
      },

      about: {
        /**
         * The page title used in the <title> tag, this provides SEO keywords for a given page
         * and is used in google analytics data provided through the GTMService.
         */
        title: undefined as unknown as string, // should be overridden by site
        /**
         * This is used as a 'description' in a meta tag in the head and is also used in google analytics
         * data provided through the GTMService. The description is not placed within the UI of the page but
         * is instead used to allow crawlers to identify the topics of the page.
         */
        description: undefined as unknown as string, // should be overridden by site
        /**
         * The URL of the site favicon.
         */
        faviconURL: undefined as unknown as string // should be overridden by site
      },

      giftCard: {
        /**
         * The page title used in the <title> tag, this provides SEO keywords for a given page
         * and is used in google analytics data provided through the GTMService.
         */
        title: undefined as unknown as string, // should be overridden by site
        /**
         * This is used as a 'description' in a meta tag in the head and is also used in google analytics
         * data provided through the GTMService. The description is not placed within the UI of the page but
         * is instead used to allow crawlers to identify the topics of the page.
         */
        description: undefined as unknown as string, // should be overridden by site
        /**
         * Settings for search engine crawlers.
         *
         * @see https://developers.google.com/search/docs/crawling-indexing/robots/intro
         * @see https://nextjs.org/docs/app/api-reference/functions/generate-metadata#robots
         */
        robots: {
          /**
           * If this page should be indexed by search engines.
           */
          index: true
        }
      },

      /** Settings related to the sitemap. */
      sitemap: {
        /**
         * The base URL of the API Gateway the Sitemap Getter is deployed on.
         */
        apiGatewayURL: 'https://36xt094zoe.execute-api.us-west-2.amazonaws.com',

        /** The version of the API to use. */
        apiGatewayVersion: 'prod',

        /** The endpoint Sitemap Getter is deployed on. */
        apiGatewayEndpoint: 'sitemap-getter',

        /** The API key to use with the Sitemap Getter API. */
        apiKey: SITEMAP_GETTER_API_KEY as string,

        /** The value to send on the `Cache-Control` header. */
        cacheControlHeaderValue:
          'public, max-age=86400, s-maxage=86400, stale-while-revalidate=86400'
      },
      bureauOfAdventure: {
        'default-page': {
          title: undefined as unknown as string, // should be overridden by site
          description: undefined as unknown as string, // should be overridden by site
          robots: { index: true }
        },
        'alvin-dedeaux': {
          title: undefined as unknown as string, // should be overridden by site
          description: undefined as unknown as string, // should be overridden by site
          robots: { index: true }
        },
        'shyanne-orvis': {
          title: undefined as unknown as string, // should be overridden by site
          description: undefined as unknown as string, // should be overridden by site
          robots: { index: true }
        },
        'mike-wardian': {
          title: undefined as unknown as string, // should be overridden by site
          description: undefined as unknown as string, // should be overridden by site
          robots: { index: true }
        },
        'evy-leibfarth': {
          title: undefined as unknown as string, // should be overridden by site
          description: undefined as unknown as string, // should be overridden by site
          robots: { index: true }
        },
        'erin-ton': {
          title: undefined as unknown as string, // should be overridden by site
          description: undefined as unknown as string, // should be overridden by site
          robots: { index: true }
        },
        'dane-jackson': {
          title: undefined as unknown as string, // should be overridden by site
          description: undefined as unknown as string, // should be overridden by site
          robots: { index: true }
        },
        'brigette-takeuchi': {
          title: undefined as unknown as string, // should be overridden by site
          description: undefined as unknown as string, // should be overridden by site
          robots: { index: true }
        }
      },
      collaborations: {
        title: undefined as unknown as string,
        description: undefined as unknown as string,
        robots: { index: true }
      },
      environment: {
        title: undefined as unknown as string,
        description: undefined as unknown as string,
        robots: { index: true }
      },
      materials: {
        title: undefined as unknown as string,
        description: undefined as unknown as string,
        robots: { index: true }
      },
      'our-commitment': {
        title: undefined as unknown as string,
        description: undefined as unknown as string,
        robots: { index: true }
      },
      'our-impact': {
        title: undefined as unknown as string,
        description: undefined as unknown as string,
        robots: { index: true }
      },
      'our-perspective': {
        title: undefined as unknown as string,
        description: undefined as unknown as string,
        robots: { index: true }
      },
      people: {
        title: undefined as unknown as string,
        description: undefined as unknown as string,
        robots: { index: true }
      }
    }
  },
  'SANUK-US': {
    default: {
      home: {
        title: 'Sidewalk Surfers®, Sandals, Shoes, and More! | Sanuk®',
        description:
          'Get a head start on the journey to your happy place with Sanuk® sidewalker surfers, sandals, shoes, yoga slings, and flip flops!',
        faviconURL:
          'https://www.sanuk.com/on/demandware.static/Sites-SANUK-US-Site/-/default/dw29a0abd1/images/favicon.ico'
      }
    }
  },
  'AHNU-US': {
    default: {
      home: {
        title: 'AHNU® | Always Forward | Performance Footwear',
        description:
          'Discover AHNU®. Always forward. Performance footwear blending uncompromised design with precise engineering. Made for those who never slow down.'
      },
      about: {
        title: 'Discover AHNU: About Us | AHNU®',
        description:
          'AHNU®, from the minds of UGG and HOKA, offers footwear that combines performance with style, focusing on comfort, quality, and style.'
      }
    }
  },
  'TEVA-US': {
    default: {
      home: {
        title:
          'Fashionable Active Sandals, River Shoes, Boots, & More | Teva®',
        description:
          'Pioneered in 1984, Teva® is your guide for adventure from the everyday to the epic. Visit the Teva® Official Site for shoes, active sandals, hikers, boots, and more.'
      },
      about: {
        title: 'Discover TEVA: About Us | TEVA®',
        description:
          'TEVA®, from the minds of UGG and HOKA, offers footwear that combines performance with style, focusing on comfort, quality, and style.'
      },
      giftCard: {
        title: 'Digital Gift Card | Teva®',
        description:
          'Purchase a Teva® digital gift card for the last-minute gifting'
      },
      bureauOfAdventure: {
        'default-page': {
          title: 'Meet the Bureau of Adventure',
          description:
            'Meet the Bureau of Adventure, a group of athletes who are passionate about adventure and pushing the boundaries of what is possible.'
        },
        'alvin-dedeaux': {
          title: 'Meet Alvin Dedeaux',
          description:
            'Texas-based fly-fishing guide Alvin Dedeaux has been running rivers for nearly 50 years, building a legacy on his angling skills and conservation advocacy.'
        },
        'shyanne-orvis': {
          title: 'Meet Shyanne Orvis',
          description:
            "Originally from Flint, Michigan and now based in Carbondale, Colorado, Shyanne Orvis is a fly-fishing guide, travel host, and children's book author dedicated to connecting people to wild places."
        },
        'mike-wardian': {
          title: 'Meet Mike Wardian',
          description: `Whether it's running backyard ultramarathons, local marathons & 5ks, setting World Records or completing multi-day treks across multiple countries, Mike Wardian is a relentless force of endurance and positivity.`
        },
        'evy-leibfarth': {
          title: 'Meet Evy Leibfarth',
          description: `Olympic kayaker Evy Leibfarth has been chasing whitewater adventures—and world titles—since she was 12. Now based in Charlotte, NC, she competes in slalom, kayak, and kayak cross, blending adrenaline with precision.`
        },
        'erin-ton': {
          title: 'Meet Erin Ton',
          description: `Colorado-born mountain endurance athlete Erin Ton spends most of her time trail running and scrambling alpine ridgelines across the West. She's set well over 100 FKTs on peaks from Colorado 14ers, to the Grand Canyon, and the Himalayas.`
        },
        'dane-jackson': {
          title: 'Meet Dane Jackson',
          description: `Dane Jackson has spent his life on the water in search of the best whitewater and waterfalls this planet has to offer, all in his signature pink kayak. A five-time Freestyle World Champion with first descents everywhere from Madagascar to Pakistan.`
        },
        'brigette-takeuchi': {
          title: 'Meet Brigette Takeuchi',
          description: `Brigette Takeuchi is an all-around mountain athlete who thrives on movement—running trails, skiing fresh lines, and capturing it all behind the lens. Whether chasing FKT's or traversing remote alpine ridges, she brings grit, heart, and a creative perspective to every outdoor adventure.`
        }
      },
      collaborations: {
        title: 'Collaborations | TEVA®',
        description: 'Teva Brand Collaborations',
        robots: { index: true }
      },
      environment: {
        title: 'Environment | TEVA®',
        description: 'Teva Environment Responsibility Page',
        robots: { index: true }
      },
      materials: {
        title: 'Materials | TEVA®',
        description: 'Teva Materials Page',
        robots: { index: true }
      },
      'our-commitment': {
        title: 'Our Commitment | TEVA®',
        description: 'Teva Commitment Page',
        robots: { index: true }
      },
      'our-impact': {
        title: 'Our Impact | TEVA®',
        description: 'Teva Impact Page',
        robots: { index: true }
      },
      'our-perspective': {
        title: 'Our Perspective | TEVA®',
        description: 'Teva Perspective Page',
        robots: { index: true }
      },
      people: {
        title: 'People | TEVA®',
        description: 'Teva People Page',
        robots: { index: true }
      }
    }
  },
  'UGG-COLLAB-US': {
    default: {
      home: {
        title:
          'UGG® Official | Boots, Slippers & Shoes | Free Shipping & Returns',
        description: `It's always UGG® season. Find the perfect boots, slippers, sneakers, and sandals to complete your look - from statement fluffy platforms to cozy house shoes, we have you covered.`
      },
      about: {
        title: 'Discover UGG: About Us | UGG®',
        description: `It's always UGG® season. Find the perfect boots, slippers, sneakers, and sandals to complete your look - from statement fluffy platforms to cozy house shoes, we have you covered.`
      }
    }
  },
  'UGG-US': {
    default: {
      home: {
        title:
          'UGG® Official | Boots, Slippers & Shoes | Free Shipping & Returns',
        description: `It's always UGG® season. Find the perfect boots, slippers, sneakers, and sandals to complete your look - from statement fluffy platforms to cozy house shoes, we have you covered.`
      },
      about: {
        title: 'Discover UGG: About Us | UGG®',
        description: `It's always UGG® season. Find the perfect boots, slippers, sneakers, and sandals to complete your look - from statement fluffy platforms to cozy house shoes, we have you covered.`
      }
    }
  },
  'KOOLABURRA-US': {
    default: {
      home: {
        title: 'Koolaburra by UGG® Official Site | Koolaburra.com',
        description: `Shop Koolaburra by UGG® for women's and kids' boots, shoes, slippers, and more in a variety of silhouettes, heights, and colors.`
      },
      about: {
        title: 'Koolaburra by UGG® Official Site | Koolaburra.com',
        description: `Shop Koolaburra by UGG® for women's and kids' boots, shoes, slippers, and more in a variety of silhouettes, heights, and colors.`
      }
    }
  },
  'HOKA-US': {
    default: {
      home: {
        title: 'Cushioned Running Shoes & Performance Wear | HOKA®',
        description: `Shop HOKA® for maximum cushion and minimal weight in our fitness, trail, hiking, and road running shoes. Finish the look in our performance wear. FLY HUMAN FLY™.`
      },
      about: {
        title: 'About the HOKA Brand | HOKA®',
        description: `HOKA's purpose is to inspire people to move. We create innovative and unexpected new solutions for athletes of all types using an &quot;always-on&quot; approach.`
      }
    }
  },
  'CONDUCTOR-US': {
    default: {
      home: {
        title: 'Welcome',
        description:
          'A Deckers internal only tool to generate an aggregate view of in-flight serverless operations & configure the various deployed builds.'
      }
    }
  }
});

export default metadata;
